import { Injectable, Injector, inject, signal } from '@angular/core';

import { LOGGING_OUT_KEY, LogoutService } from '../../auth/logout.service';
import { SessionStorageService } from '../../storage/session-storage.service';

export const SESSION_SUPERSEDED_MESSAGE =
  'This account was logged in on another device. This session has been ended.';

// Keeps the superseded state across a reload, so refreshing past the dialog re-shows it
// at once instead of leaving the app usable until the next failed poll.
const SESSION_SUPERSEDED_KEY = 'cv.sessionSuperseded';

/**
 * Single-active-session enforcement (see docs/plans/2026-07-29-single-active-session-design.md).
 * Surfaces the "logged in elsewhere" blocking dialog when horizon-service rejects a call with
 * 409 `SESSION_SUPERSEDED` — a newer login on another browser has taken over this account.
 *
 * Mirrors `SessionExpiryService`'s idempotent signal shape, but deliberately does NOT reuse it:
 * `SessionExpiryService.acknowledge()` skips the real `/v1/logout` call and redirects straight
 * to SSO login. Here, acknowledging calls the REAL `LogoutService.logout()` — this device's
 * presence/online state should be cleanly cleared on the way out, not just abandoned locally.
 *
 * `LogoutService` is resolved lazily via `Injector`, not a direct field `inject()`: this service
 * is constructed eagerly in `App`'s constructor, and eagerly injecting `LogoutService` there drags
 * its own eager `UsageLoggingService -> BulkLogService -> LstUserService` chain along with it,
 * which re-enters `LstUserService` construction and trips Angular's NG0200 guard.
 * `LogoutService` itself uses this same lazy-`Injector` pattern for its lst-auth dependencies for
 * the same reason — see its `notifyBackendLogout`/`redirectToLogout`.
 *
 * `notifySuperseded()` no-ops once `LOGGING_OUT_KEY` is set: `acknowledge()`'s own call to
 * `LogoutService.logout()` makes several more horizon-service calls (heartbeat, planner
 * session teardown) as this — now superseded — device's own last acts, and any one of those
 * getting rejected would otherwise re-open this same dialog a moment before the page
 * redirects away.
 */
@Injectable({ providedIn: 'root' })
export class SessionSupersededService {
  private readonly injector = inject(Injector);
  private readonly sessionStorage = inject(SessionStorageService);

  private readonly _superseded = signal(this.sessionStorage.get(SESSION_SUPERSEDED_KEY) === 'true');
  readonly superseded = this._superseded.asReadonly();
  readonly message = signal(SESSION_SUPERSEDED_MESSAGE);

  notifySuperseded(): void {
    if (this._superseded()) return;
    if (this.sessionStorage.get(LOGGING_OUT_KEY) === 'true') return;
    this._superseded.set(true);
    this.sessionStorage.set(SESSION_SUPERSEDED_KEY, 'true');
  }

  acknowledge(): void {
    if (!this._superseded()) return;
    this._superseded.set(false);
    this.sessionStorage.remove(SESSION_SUPERSEDED_KEY);
    this.injector.get(LogoutService).logout();
  }
}
